import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Modal built on the native <dialog> element — real focus trapping and Esc
 * handling for free, no portal library. Dialogs are the one place shadows are
 * allowed (see docs/DESIGN.md).
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  className,
  busy = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * Refuses Escape, the backdrop and the X while an action is in flight.
   *
   * Without it a dialog that has already fired an irreversible request — a
   * reset destroys a sitting's recordings — vanishes on Escape and reads as
   * cancelled, while the delete completes anyway. Disabling only the Cancel
   * button is not enough; the other three exits are the ones people reach for
   * when they panic.
   */
  busy?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const requestClose = () => {
    if (!busy) onClose();
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        // Always prevented: the native close would bypass `busy` entirely.
        e.preventDefault();
        requestClose();
      }}
      onClose={requestClose}
      onClick={(e) => {
        // Clicking the ::backdrop targets the dialog element itself.
        if (e.target === ref.current) requestClose();
      }}
      className={cn(
        "m-auto w-[min(32rem,calc(100vw-2rem))] rounded-md border border-border",
        "bg-surface p-0 text-ink shadow-overlay",
        "backdrop:bg-ink/25",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={requestClose}
          disabled={busy}
          aria-label="Close"
          className="rounded-sm p-1 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink disabled:opacity-40"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {children ? <div className="px-4 py-4 text-sm">{children}</div> : null}

      {footer ? (
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
